import { fetchWithAuth } from './api';

const host = window.location.hostname === 'localhost' ? "http://localhost:8000/api" : "/api";

/**
 * Fetches all negotiation workspaces
 * @param negotiationWorkspaceName - Optional filter by workspace name
 * @param orderBy - Optional order by parameter ('name' or undefined for date)
 * @returns Promise with the list of negotiation workspaces
 */
export const getNegotiationWorkspaces = async (negotiationWorkspaceName?: string, orderBy?: string) => {
  let url = host + '/negotiation/workspaces';
  const params = new URLSearchParams();
  
  if (negotiationWorkspaceName) {
    params.append('negotiation_workspace_name', negotiationWorkspaceName);
  }
  
  if (orderBy) {
    params.append('order_by', orderBy);
  }
  
  const queryString = params.toString();
  if (queryString) {
    url += '?' + queryString;
  }
  
  const response = await fetchWithAuth(url, {
    method: 'GET'
  });
  
  return await response.json();
};

/**
 * Creates a new negotiation workspace
 * @param negotiationWorkspaceName - Name of the negotiation workspace
 * @param comments - Optional comments about the negotiation
 * @returns Promise with the created negotiation workspace data
 */
export const createNegotiationWorkspace = async (negotiationWorkspaceName: string, comments?: string) => {
  const response = await fetchWithAuth(
    host + '/negotiation/workspaces',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        negotiation_workspace_name: negotiationWorkspaceName,
        comments
      })
    }
  );
  
  return await response.json();
};

/**
 * Updates a negotiation workspace
 * @param negotiationId - ID of the negotiation to update
 * @param comments - New comments for the negotiation
 * @returns Promise with the update result
 */
export const updateNegotiationWorkspace = async (negotiationId: number, comments?: string) => {
  const response = await fetchWithAuth(
    host + `/negotiation/workspaces/${negotiationId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        comments
      })
    }
  );
  
  return await response.json();
};

/**
 * Deletes a negotiation workspace
 * @param negotiationWorkspaceId - ID of the negotiation workspace to delete
 * @returns Promise with the deletion result
 */
export const deleteNegotiationWorkspace = async (negotiationWorkspaceId: number) => {
  const response = await fetchWithAuth(
    host + `/negotiation/workspaces/${negotiationWorkspaceId}`,
    {
      method: 'DELETE'
    }
  );
  
  return await response.json();
};

/**
 * Fetches negotiation spend data for a specific contract ID
 * @param contractId - The ID of the contract to fetch spend data for
 * @returns Promise with the spend data array
 */
export const getNegotiationSpendData = async (contractId: number) => {
  contractId = 78;
  const response = await fetchWithAuth(
    host + `/negotiation/spend/${contractId}`,
    {
      method: 'GET'
    }
  );
  
  const data = await response.json();
  return data;
};

/**
 * Fetches tender summary data
 * @returns Promise with the tender summary array
 */
export const getNegotiationTenderSummary = async () => {
  // Hardcoded contract ID 78 as requested
  const contractId = 78;
  const response = await fetchWithAuth(
    host + `/negotiation/tendersummary/${contractId}`,
    {
      method: 'GET'
    }
  );
  
  const data = await response.json();
  return data;
};

/**
 * Fetches red flags data for supplier evaluation
 * @returns Promise with the red flags array
 */
export const getNegotiationRedFlags = async () => {
  // Hardcoded contract ID 78 as requested
  const contractId = 78;
  const response = await fetchWithAuth(
    host + `/negotiation/redflag/${contractId}`,
    {
      method: 'GET'
    }
  );
  
  const data = await response.json();
  return data;
};

/**
 * Fetches supplier bid data for comparison in the Tender Summary tab
 * @returns Promise with the supplier bid data array
 */
export const getSupplierBidData = async () => {
  // Hardcoded contract ID 78 as requested
  const contractId = 78;
  const response = await fetchWithAuth(
    host + `/negotiation/yoydata/${contractId}`,
    {
      method: 'GET'
    }
  );
  
  const data = await response.json();
  return data;
};

/**
 * Fetches historical data for should-cost modeling
 * @returns Promise with the historical data array
 */
export const getHistoricalData = async () => {
  // Hardcoded contract ID 78 as requested
  const contractId = 78;
  const response = await fetchWithAuth(
    host + `/negotiation/historicaldata/${contractId}`,
    {
      method: 'GET'
    }
  );
  
  const data = await response.json();
  return data;
};

/**
 * Fetches market price data for comparison with supplier prices
 * @returns Promise with the market price data array
 */
export const getMarketPriceData = async () => {
  // Hardcoded contract ID 78 as requested
  const contractId = 78;
  const response = await fetchWithAuth(
    host + `/negotiation/market_price/${contractId}`,
    {
      method: 'GET'
    }
  );
  
  const data = await response.json();
  return data;
};
